import { Button } from '@/components/ui/Button'
import { IconCash, IconMinus, IconPlus, IconTransfer } from '@/components/icons'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { PosPaymentMethod } from './posApi'

export type PosCartLine = {
  productId: number
  name: string
  price: number
  quantity: number
}

type Props = {
  saleStep: 'edit' | 'review'
  cartLines: PosCartLine[]
  currency: string
  storeName?: string | null
  subtotal: number
  taxTotal: number
  grandTotal: number
  paymentMethod: PosPaymentMethod
  submitting: boolean
  storeId: number | null
  onPaymentMethodChange: (method: PosPaymentMethod) => void
  onAdjustQty: (productId: number, delta: number) => void
  onClearCart: () => void
  onCheckout: () => void
  onBackToEdit: () => void
  onFinalize: () => void
}

export function PosCartContent({
  saleStep,
  cartLines,
  currency,
  storeName,
  subtotal,
  taxTotal,
  grandTotal,
  paymentMethod,
  submitting,
  storeId,
  onPaymentMethodChange,
  onAdjustQty,
  onClearCart,
  onCheckout,
  onBackToEdit,
  onFinalize,
}: Props) {
  if (saleStep === 'review') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain">
          {storeName ? (
            <p className="text-muted-foreground text-sm">
              Store: <span className="text-foreground font-medium">{storeName}</span>
            </p>
          ) : null}
          <div>
            <h3 className="text-foreground mb-3 text-sm font-semibold">Summary</h3>
            <ul className="space-y-2.5" aria-label="Sale summary">
              {cartLines.map((l) => {
                const lineTotal = l.price * l.quantity
                return (
                  <li
                    key={l.productId}
                    className="border-border flex items-start justify-between gap-3 rounded-xl border bg-muted/10 px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground text-sm font-medium leading-snug">{l.name}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
                        {l.quantity} × {formatMoney(l.price, currency)}
                      </p>
                    </div>
                    <p className="text-foreground shrink-0 text-sm font-semibold tabular-nums">
                      {formatMoney(lineTotal, currency)}
                    </p>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="border-border space-y-2.5 border-t border-dashed pt-4 text-sm">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{formatMoney(subtotal, currency)}</span>
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-muted-foreground">Tax</span>
              <span className="tabular-nums">{formatMoney(taxTotal, currency)}</span>
            </div>
            <div className="border-border flex items-baseline justify-between gap-4 border-t pt-3 text-base font-semibold">
              <span>Total</span>
              <span aria-live="polite" className="tabular-nums text-lg">
                {formatMoney(grandTotal, currency)}
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-muted/15 px-3 py-3">
            <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
              Payment method
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm font-semibold">
              {paymentMethod === 'cash' ? (
                <IconCash className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
              ) : (
                <IconTransfer className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
              )}
              {paymentMethod === 'cash' ? 'Cash' : 'Transfer'}
            </div>
          </div>
        </div>
        <div className="border-border mt-4 flex shrink-0 flex-col gap-3 border-t pt-4">
          <Button
            type="button"
            variant="secondary"
            className="min-h-[3.25rem] w-full text-base font-semibold"
            disabled={submitting}
            onClick={onBackToEdit}
          >
            Back to edit
          </Button>
          <Button
            className="min-h-[3.25rem] w-full text-base font-semibold shadow-sm"
            disabled={cartLines.length === 0 || submitting || storeId == null}
            onClick={onFinalize}
          >
            {submitting ? 'Saving…' : 'Finalize sale'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-foreground text-base font-semibold">Cart</h3>
        {cartLines.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" className="text-destructive min-h-9" onClick={onClearCart}>
            Clear cart
          </Button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {cartLines.length === 0 ? (
          <div className="bg-muted/20 text-muted-foreground rounded-2xl border border-dashed border-border/80 px-4 py-10 text-center text-sm">
            Tap products to add them — use + and − to adjust quantities.
          </div>
        ) : (
          <ul className="space-y-3" aria-label="Sale line items">
            {cartLines.map((l) => {
              const lineTotal = l.price * l.quantity
              return (
                <li
                  key={l.productId}
                  className="bg-muted/15 border-border/80 rounded-2xl border p-3 shadow-sm"
                >
                  <div className="flex flex-col gap-3">
                    <div className="min-w-0">
                      <p className="text-foreground truncate text-sm font-semibold leading-snug">{l.name}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
                        {formatMoney(l.price, currency)}
                        <span className="text-muted-foreground/80"> each</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div
                        className="bg-background/90 border-border flex items-center rounded-2xl border p-1 shadow-inner"
                        role="group"
                        aria-label={`Quantity for ${l.name}`}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 shrink-0 rounded-xl motion-reduce:active:scale-100"
                          aria-label={
                            l.quantity <= 1
                              ? `Remove ${l.name} from sale`
                              : `Decrease ${l.name} quantity`
                          }
                          onClick={() => onAdjustQty(l.productId, -1)}
                        >
                          <IconMinus className="h-5 w-5" aria-hidden />
                        </Button>
                        <span
                          className="text-foreground min-w-[2.5rem] px-1 text-center text-lg font-bold tabular-nums tracking-tight"
                          aria-live="polite"
                        >
                          {l.quantity}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 shrink-0 rounded-xl motion-reduce:active:scale-100"
                          aria-label={`Increase ${l.name} quantity`}
                          onClick={() => onAdjustQty(l.productId, 1)}
                        >
                          <IconPlus className="h-5 w-5" aria-hidden />
                        </Button>
                      </div>
                      <p className="text-foreground text-sm font-semibold tabular-nums">
                        {formatMoney(lineTotal, currency)}
                      </p>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="border-border mt-4 shrink-0 space-y-4 border-t pt-4">
        <div className="space-y-2 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="tabular-nums">{formatMoney(subtotal, currency)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted-foreground">Tax</span>
            <span className="tabular-nums">{formatMoney(taxTotal, currency)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-4 text-base font-semibold">
            <span>Total</span>
            <span className="tabular-nums text-xl">{formatMoney(grandTotal, currency)}</span>
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Payment method
          </span>
          <div
            className="border-border bg-muted/35 grid grid-cols-2 gap-1 rounded-2xl border p-1"
            role="radiogroup"
            aria-label="Payment method"
          >
            <button
              type="button"
              role="radio"
              aria-checked={paymentMethod === 'cash'}
              className={cn(
                'flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition-colors motion-reduce:transition-none',
                'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                paymentMethod === 'cash'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/95 hover:text-foreground',
              )}
              onClick={() => onPaymentMethodChange('cash')}
            >
              <IconCash className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
              Cash
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={paymentMethod === 'transfer'}
              className={cn(
                'flex min-h-[3.25rem] items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition-colors motion-reduce:transition-none',
                'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                paymentMethod === 'transfer'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/95 hover:text-foreground',
              )}
              onClick={() => onPaymentMethodChange('transfer')}
            >
              <IconTransfer className="h-5 w-5 shrink-0 opacity-90" aria-hidden />
              Transfer
            </button>
          </div>
        </div>

        <Button
          className="min-h-[3.25rem] w-full text-base font-semibold shadow-sm"
          disabled={cartLines.length === 0 || submitting || storeId == null}
          onClick={onCheckout}
        >
          Checkout
        </Button>
      </div>
    </div>
  )
}
